import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { setUserBlocked, unflagPassengerForPrank, adminDeleteAccount, formatTime, phoneHref, waHref } from '../services/firebase';
import { downloadCsv } from '../utils/exportCsv';
import DeleteAccountModal from '../components/DeleteAccountModal';
import { useLang } from '../lang/LangContext';

const PAGE_SIZE = 100;

export default function Passengers({ users = [], rides = [], isSuperAdmin = false }) {
  const { t } = useLang();
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Compute Passenger History Metrics (Module G)
  const userMetrics = useMemo(() => {
    const ridesByCustomer = {};
    rides.forEach((ride) => {
      if (!ride.customerId) return;
      if (!ridesByCustomer[ride.customerId]) ridesByCustomer[ride.customerId] = [];
      ridesByCustomer[ride.customerId].push(ride);
    });

    const map = {};
    users.forEach((u) => {
      const uRides = ridesByCustomer[u.id] || [];
      const completed = uRides.filter((r) => r.status === 'completed');
      const cancelled = uRides.filter((r) => r.status === 'cancelled');
      const totalSpend = completed.reduce((s, r) => s + (Number(r.fare) || 0), 0);

      map[u.id] = {
        totalRides: uRides.length,
        completedCount: completed.length,
        cancelledCount: cancelled.length,
        totalSpend,
        rides: uRides,
      };
    });
    return map;
  }, [users, rides]);

  // Derived live passenger object (100% reactive to Firestore updates while open)
  const selectedUser = useMemo(() => {
    if (!selectedUserId) return null;
    const u = users.find((user) => user.id === selectedUserId);
    if (!u) return null;
    return {
      ...u,
      metrics: userMetrics[selectedUserId] || { totalRides: 0, completedCount: 0, cancelledCount: 0, totalSpend: 0, rides: [] },
    };
  }, [selectedUserId, users, userMetrics]);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return users.filter((u) => {
      if (filter === 'blocked' && !u.isBlocked) return false;
      if (filter === 'active' && u.isBlocked) return false;
      if (filter === 'prank' && !u.isFlaggedForPrank) return false;
      if (!qq) return true;
      const hay = [u.name, u.phone, u.id].join(' ').toLowerCase();
      return hay.includes(qq);
    });
  }, [users, q, filter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const visiblePassengers = filtered.slice(pageStart, pageStart + PAGE_SIZE);

  const exportRows = () => {
    downloadCsv('passengers.csv', [
      ['id', 'name', 'phone', 'totalRides', 'completed', 'cancelled', 'totalSpend', 'strikes', 'isFlaggedForPrank', 'blocked', 'joined'],
      ...filtered.map((u) => {
        const m = userMetrics[u.id] || {};
        return [
          u.id, u.name, u.phone, m.totalRides || 0, m.completedCount || 0, m.cancelledCount || 0,
          m.totalSpend || 0, u.consecutiveCancellations || 0, u.isFlaggedForPrank ? 'yes' : 'no',
          u.isBlocked ? 'yes' : 'no', formatTime(u.createdAt),
        ];
      }),
    ]);
  };

  const toggleBlock = async (u) => {
    const next = !u.isBlocked;
    if (next && !window.confirm(`Block ${u.name || u.phone}?`)) return;
    setBusy(u.id);
    try {
      await setUserBlocked(u.id, next);
      toast.success(next ? 'Blocked' : 'Unblocked');
    } catch (e) {
      console.error(e);
      toast.error('Failed');
    } finally {
      setBusy(null);
    }
  };

  const unflagPrank = async (uid) => {
    setBusy(uid);
    try {
      await unflagPassengerForPrank(uid);
      toast.success('Passenger unflagged & booking access restored!');
    } catch {
      toast.error('Failed to unflag passenger');
    } finally {
      setBusy(null);
    }
  };

  const confirmDelete = async (confirmPhone) => {
    if (!deleteTarget?.id) return;
    setDeleting(true);
    try {
      const res = await adminDeleteAccount(deleteTarget.id, 'passenger', confirmPhone);
      const n = Number(res?.cancelledRides) || 0;
      toast.success(n ? `Passenger deleted (${n} active ride cancelled)` : 'Passenger deleted');
      setDeleteTarget(null);
    } catch (e) {
      console.error(e);
      const msg = e?.message || '';
      if (msg.includes('Phone does not match')) toast.error('Phone does not match');
      else if (msg.includes('not-found') || msg.includes('Passenger not found')) toast.error('Passenger not found');
      else toast.error(msg.replace(/^Firebase:\s*/i, '').slice(0, 120) || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const prankFlaggedCount = users.filter((u) => u.isFlaggedForPrank).length;

  return (
    <>
      <div className="toolbar">
        <select value={filter} onChange={(e) => { setFilter(e.target.value); setPage(1); }}>
          <option value="all">{t('All Passengers')} ({users.length})</option>
          <option value="prank">🚫 Prank / 3-Strike Flagged ({prankFlaggedCount})</option>
          <option value="active">{t('Active')}</option>
          <option value="blocked">{t('Blocked')}</option>
        </select>
        <input placeholder={t('Search name or phone…')} value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
        <span className="muted">{filtered.length} passengers</span>
        <button type="button" className="btn btn-ghost btn-sm" onClick={exportRows}>{t('Export CSV')}</button>
      </div>

      <div className="card">
        {filtered.length === 0 ? (
          <div className="empty">{t('No passengers match filter')}</div>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>{t('PASSENGER')}</th>
                  <th>{t('PHONE')}</th>
                  <th>{t('RIDES & SPEND')}</th>
                  <th>{t('HOME / WORK')}</th>
                  <th>{t('SAFETY STATUS')}</th>
                  <th>{t('JOINED')}</th>
                  <th>{t('ACTIONS')}</th>
                </tr>
              </thead>
              <tbody>
                {visiblePassengers.map((u) => {
                  const m = userMetrics[u.id] || {};
                  return (
                    <tr key={u.id}>
                      <td>
                        <strong style={{ color: '#f8fafc' }}>{u.name || 'Passenger'}</strong>
                      </td>
                      <td className="mono">{u.phone || '—'}</td>
                      <td>
                        <div><strong>{m.completedCount || 0} completed</strong> · ₹{m.totalSpend || 0} spend</div>
                        <div className="muted" style={{ fontSize: 11 }}>{m.cancelledCount || 0} cancellations</div>
                      </td>
                      <td className="muted" style={{ fontSize: 12 }}>
                        {u.savedPlaces?.home?.address ? `🏠 ${u.savedPlaces.home.address}` : '—'}
                        {u.savedPlaces?.work?.address && <><br />🏢 {u.savedPlaces.work.address}</>}
                      </td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {u.isBlocked && <span className="badge red">{t('Blocked')}</span>}
                          {u.isFlaggedForPrank && (
                            <span className="badge red" title="Flagged for 3 consecutive cancellations">
                              🚫 3-Strike Cooldown
                            </span>
                          )}
                          {!u.isBlocked && !u.isFlaggedForPrank && (
                            <span className="badge green">{t('Good Standing')}</span>
                          )}
                        </div>
                      </td>
                      <td className="mono muted" style={{ fontSize: 12 }}>{formatTime(u.createdAt)}</td>
                      <td>
                        <div className="row-actions">
                          {isSuperAdmin && u.isFlaggedForPrank && (
                            <button
                              type="button"
                              className="btn btn-primary btn-sm"
                              style={{ background: '#10b981', borderColor: '#10b981', fontSize: 11 }}
                              disabled={busy === u.id}
                              onClick={() => unflagPrank(u.id)}
                            >
                              ✓ Unflag
                            </button>
                          )}
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            onClick={() => setSelectedUserId(u.id)}
                          >
                            History
                          </button>
                          {isSuperAdmin && (
                            <button
                              type="button"
                              className={`btn btn-sm ${u.isBlocked ? 'btn-green' : 'btn-red'}`}
                              disabled={busy === u.id}
                              onClick={() => toggleBlock(u)}
                            >
                              {u.isBlocked ? 'Unblock' : 'Block'}
                            </button>
                          )}
                          {phoneHref(u.phone) && <a className="btn btn-ghost btn-sm" href={phoneHref(u.phone)}>{t('Call')}</a>}
                          {waHref(u.phone) && <a className="btn btn-ghost btn-sm" href={waHref(u.phone)} target="_blank" rel="noreferrer">WA</a>}
                          {isSuperAdmin && (
                            <button
                              type="button"
                              className="btn btn-ghost btn-sm"
                              style={{ color: '#ef4444', borderColor: 'rgba(239, 68, 68, 0.3)' }}
                              onClick={() => setDeleteTarget(u)}
                              title="Delete passenger account"
                            >
                              🗑️
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {filtered.length > PAGE_SIZE && (
          <div className="passenger-pagination" aria-label="Passenger list pagination">
            <span className="muted">
              Showing {pageStart + 1}–{Math.min(pageStart + PAGE_SIZE, filtered.length)} of {filtered.length}
            </span>
            <div className="row-actions">
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                disabled={currentPage === 1}
                onClick={() => setPage(Math.max(1, currentPage - 1))}
              >
                ← Previous
              </button>
              <span className="muted">Page {currentPage} of {totalPages}</span>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                disabled={currentPage === totalPages}
                onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Selected Passenger History Modal — a real popup (not an inline card),
          so it's always front-and-center regardless of where you clicked
          History in a long list, and it's unambiguous which passenger it's
          showing (name + phone right in the title). Click outside or ✕ to close. */}
      {selectedUser && (
        <div
          className="modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="passenger-history-title"
          onClick={() => setSelectedUserId(null)}
        >
          <div className="modal-sheet" style={{ maxWidth: 680, maxHeight: '85vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <div className="card-title" id="passenger-history-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>👤 Passenger Profile · {selectedUser.name || 'Passenger'} ({selectedUser.phone})</span>
              <div style={{ display: 'flex', gap: 8 }}>
                {isSuperAdmin && (
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    style={{ color: '#ef4444', borderColor: 'rgba(239, 68, 68, 0.3)' }}
                    onClick={() => setDeleteTarget(selectedUser)}
                  >
                    🗑️ Delete Account
                  </button>
                )}
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setSelectedUserId(null)}>✕ Close</button>
              </div>
            </div>
            <div className="detail-grid" style={{ marginBottom: 16 }}>
              <div><strong>TOTAL COMPLETED</strong>{selectedUser.metrics.completedCount} trips</div>
              <div><strong>TOTAL SPENT</strong>₹{selectedUser.metrics.totalSpend}</div>
              <div><strong>CANCELLATIONS</strong>{selectedUser.metrics.cancelledCount} trips</div>
              <div><strong>STRIKES</strong>{selectedUser.consecutiveCancellations || 0} recorded</div>
            </div>

            <div className="card-title" style={{ fontSize: 14 }}>Recent Ride Bookings ({selectedUser.metrics.rides.length})</div>
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>STATUS</th>
                    <th>ROUTE</th>
                    <th>FARE</th>
                    <th>CAPTAIN</th>
                    <th>DATE</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedUser.metrics.rides.slice(0, 10).map((r) => (
                    <tr key={r.id}>
                      <td><span className="badge blue">{r.status}</span></td>
                      <td>{r.pickup?.address} → {r.destination?.name}</td>
                      <td><strong>₹{r.fare}</strong></td>
                      <td>{r.driverName || '—'} ({r.vehicleNumber || '—'})</td>
                      <td className="mono">{formatTime(r.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <DeleteAccountModal
        open={Boolean(deleteTarget)}
        role="passenger"
        account={deleteTarget}
        busy={deleting}
        onClose={() => { if (!deleting) setDeleteTarget(null); }}
        onConfirm={confirmDelete}
      />
    </>
  );
}
